<template>
    <div class="tab-content">
        <div v-if="referenceLoading" class="loading-overlay">
            <div class="loading-content">
                <div class="loading-spinner"></div>
                <div class="loading-text">Поиск похожих планировок...</div>
            </div>
        </div>

        <div class="controls">
            <div class="filters-container">
                <FilterGroup
                    :group="rootGroup"
                    :filters-config="filtersConfig"
                    :depth="0"
                    :path="[]"
                    @add-condition="addCondition"
                    @add-group="addGroup"
                    @remove-condition="removeCondition"
                    @remove-group="removeGroup"
                />
            </div>

            <div class="filter-buttons">
                <button class="btn secondary" @click="addCondition()">+ Добавить условие</button>
                <button class="btn secondary" @click="addGroup()">+ Добавить группу</button>
                <button class="btn" :disabled="loading || referenceLoading" @click="performSearch">
                    <span v-if="loading">Поиск...</span>
                    <span v-else>Найти</span>
                </button>
            </div>

            <div class="stats">
                <div class="stat-item">
                    <span class="stat-label">Всего планировок</span>
                    <span class="stat-value">{{ totalDocumentsCount }}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Диапазон дат</span>
                    <span class="stat-value">{{ dateRange }}</span>
                </div>
                <div class="stat-item">
                    <span class="stat-label">Найдено</span>
                    <span class="stat-value">{{ searchResults.total_count || 0 }}</span>
                </div>
            </div>
        </div>

        <ResultsGrid :items="searchResults.items || []" />

        <Pagination
            :current-page="currentPage"
            :has-next="!!searchResults.next"
            :has-prev="!!searchResults.prev"
            @prev="goToPage('prev')"
            @next="goToPage('next')"
        />
    </div>
</template>

<script>
    import { ref, onMounted, watch } from 'vue'
    import { useRoute } from 'vue-router'
    import api from '../services/api'
    import ResultsGrid from './ResultsGrid.vue'
    import Pagination from './Pagination.vue'
    import FilterGroup from './FilterGroup.vue'
    import { formatDate } from '@/utils/dateFormatter.js'

    export default {
        components: { ResultsGrid, Pagination, FilterGroup },
        setup() {
            const route = useRoute()
            const rootGroup = ref({
                id: 'root',
                type: 'group',
                logicalOperator: 'and',
                children: []
            })
            const filtersConfig = ref([])
            const totalDocumentsCount = ref('Загрузка...')
            const dateRange = ref('Загрузка...')
            const searchResults = ref({ items: [] })
            const currentPage = ref(1)
            const nextCursor = ref(null)
            const prevCursor = ref(null)
            const loading = ref(false)
            const referenceLoading = ref(false)

            const addCondition = (path = []) => {
                const newCondition = {
                    id: Date.now(),
                    type: 'condition',
                    field: '',
                    operator: 'eq',
                    value: '',
                    tolerance: null,
                    logicalOperator: 'and'
                }

                const parent = getParent(path)
                if (!parent.children) parent.children = []
                parent.children.push(newCondition)
            }

            const addGroup = (path = []) => {
                const newGroup = {
                    id: Date.now(),
                    type: 'group',
                    logicalOperator: 'and',
                    children: []
                }

                const newCondition = {
                    id: Date.now() + 1,
                    type: 'condition',
                    field: '',
                    operator: 'eq',
                    value: '',
                    tolerance: null,
                    logicalOperator: 'and'
                }
                newGroup.children.push(newCondition)

                const parent = getParent(path)
                if (!parent.children) parent.children = []
                parent.children.push(newGroup)
            }

            const removeCondition = path => {
                const parent = getParent(path.slice(0, -1))
                const index = path[path.length - 1]
                parent.children.splice(index, 1)
            }

            const removeGroup = path => {
                const parent = getParent(path.slice(0, -1))
                const index = path[path.length - 1]
                parent.children.splice(index, 1)
            }

            const getParent = path => {
                let current = rootGroup.value
                for (const index of path) {
                    if (current.children && current.children[index]) {
                        current = current.children[index]
                    }
                }
                return current
            }

            const performSearch = async direction => {
                if (rootGroup.value.children.length === 0) {
                    alert('Пожалуйста, добавьте хотя бы один фильтр')
                    return
                }

                loading.value = true
                try {
                    const body = {
                        filters: flattenFilters(rootGroup.value.children),
                        limit: 12
                    }

                    if (direction === 'next' && nextCursor.value) {
                        body.next = nextCursor.value
                    } else if (direction === 'prev' && prevCursor.value && currentPage.value > 1) {
                        body.prev = prevCursor.value
                    }

                    const data = await api.performSearch(body)
                    searchResults.value = data
                    nextCursor.value = data.next
                    prevCursor.value = data.prev

                    if (!direction) {
                        currentPage.value = 1
                    }
                } catch (error) {
                    console.error('Search error:', error)
                    alert('Ошибка при выполнении поиска')
                } finally {
                    loading.value = false
                }
            }

            const flattenFilters = items => {
                const result = []

                const processItem = (item, groupDepth) => {
                    if (item.type === 'condition') {
                        result.push({
                            field: item.field,
                            operator: item.operator,
                            value: item.value,
                            tolerance:
                                item.operator === 'range' ? parseFloat(item.tolerance) : undefined,
                            logical_operator: item.logicalOperator
                        })
                    } else if (item.type === 'group') {
                        result.push({
                            logical_operator: 'start_group',
                            logical_group: item.logicalOperator
                        })

                        item.children.forEach(child => processItem(child, groupDepth + 1))

                        result.push({
                            logical_operator: 'end_group'
                        })
                    }
                }

                items.forEach(item => processItem(item, 0))
                return result
            }

            const goToPage = direction => {
                if (direction === 'next' && searchResults.value.next) {
                    currentPage.value++
                    performSearch('next')
                } else if (direction === 'prev' && currentPage.value > 1) {
                    currentPage.value--
                    performSearch('prev')
                }
            }

            async function loadReferenceSearch(referenceId) {
                referenceLoading.value = true
                try {
                    const { data } = await api.performReferenceSearch(referenceId, 12)

                    rootGroup.value.children = data.reference_filters.map(f => ({
                        id: Date.now() + Math.random(),
                        type: 'condition',
                        field: f.field,
                        operator: f.operator,
                        value: f.value,
                        tolerance: f.tolerance || null,
                        logicalOperator: 'and'
                    }))

                    searchResults.value = {
                        items: data.items,
                        next: data.next,
                        prev: data.prev,
                        total_count: data.total_count
                    }

                    currentPage.value = 1
                } catch (error) {
                    console.error('Reference search error:', error)
                    alert('Ошибка при загрузке референсного поиска')
                } finally {
                    referenceLoading.value = false
                }
            }

            watch(
                () => route.query.referenceId,
                newRefId => {
                    if (newRefId) {
                        loadReferenceSearch(newRefId)
                    }
                }
            )

            onMounted(async () => {
                try {
                    filtersConfig.value = await api.fetchFilters()

                    const totalCountData = await api.fetchTotalDocumentsCount()
                    totalDocumentsCount.value = totalCountData.totalDocumentsCount

                    const dateRangeData = await api.fetchGlobalDateRange()
                    if (dateRangeData.oldest && dateRangeData.newest) {
                        dateRange.value = `${formatDate(dateRangeData.oldest)} - ${formatDate(dateRangeData.newest)}`
                    }

                    if (route.query.referenceId) {
                        await loadReferenceSearch(route.query.referenceId)
                    } else {
                        addCondition()
                    }
                } catch (error) {
                    console.error('Initialization error:', error)
                    totalDocumentsCount.value = 'Ошибка'
                    dateRange.value = 'Ошибка'
                }
            })

            return {
                rootGroup,
                filtersConfig,
                totalDocumentsCount,
                dateRange,
                searchResults,
                currentPage,
                loading,
                referenceLoading,
                addCondition,
                addGroup,
                removeCondition,
                removeGroup,
                performSearch,
                goToPage
            }
        }
    }
</script>

<style>
    .filter-buttons {
        display: flex;
        gap: 10px;
        margin-top: 15px;
    }

    .stats {
        display: flex;
        justify-content: space-between;
        background: #f0f4ff;
        padding: 15px 20px;
        border-radius: 10px;
        margin: 20px 0 10px;
        font-size: 15px;
    }

    .stat-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
    }

    .stat-label {
        font-size: 14px;
        color: var(--secondary-color);
        margin-bottom: 5px;
    }

    .stat-value {
        font-weight: 700;
        font-size: 18px;
        color: var(--primary-color);
    }

    /* Loading Overlay Styles */
    .loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.95);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        backdrop-filter: blur(5px);
    }

    .loading-content {
        text-align: center;
        padding: 40px;
        background: white;
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        min-width: 300px;
    }

    .loading-spinner {
        width: 60px;
        height: 60px;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #3498db;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 20px;
    }

    .loading-text {
        font-size: 18px;
        color: #333;
        font-weight: 500;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
</style>
